import React, {useEffect, useMemo, useRef, useState} from 'react'
import User, {talentUpdateRequest} from "../../models/User";
import {ProgressBar} from "react-step-progress-bar";
import ReviewStep from "./steps/ReviewStep";
import ResumeUploadModal from "./ResumeUploadModal";
import ApplicationSideNavBar from "./ApplicationSideNavBar";
import {matchPath, Navigate, Route, Routes, useLocation, useNavigate, useSearchParams} from "react-router-dom";
import {useAuth} from "../../providers/AuthProvider";
import {useMutation} from "@tanstack/react-query";
import { Helmet } from "react-helmet";
import PageNotFound from "../../components/errors/PageNotFound";
import {BioStep} from "./steps";
import {Modal} from "antd";
import ReadyToSubmitProfileModal from "./ReadyToSubmitProfileModal";
import ApplicationStepper from './components/ApplicationStepper';
import { getJourneyProgress, getOptionalSuggestions, markSectionConfirmed } from './components/applicationProgress';
import ApplicationStatusStrip from './components/ApplicationStatusStrip';
import ProfileStatusBar from './components/ProfileStatusBar';
import SaveExitModal from './journey/SaveExitModal';
import SubmitApplicationModal from './journey/SubmitApplicationModal';
import { ArrowRight } from 'lucide-react';

const TalentApplication = (props) => {
    const [searchParams, setSearchParams] = useSearchParams();
    const location = useLocation();
    const navigate = useNavigate();

    const routeRef = useRef(null);
    const resumeModalRef = useRef(null);

    const {auth, update: updateAuth, talentUpdate, appropriate, logout} = useAuth();

    const [isDisabled, setIsDisabled] = useState(false);

    const [stepSubmitDisabled, setStepSubmitDisabled] = useState(false);

    // Journey pop-ups: "Save & exit" summary and the two-step application submit
    const [saveExitOpen, setSaveExitOpen] = useState(false);
    const [appSubmitStep, setAppSubmitStep] = useState(null); // 'confirm' | 'submitted' | null

    const isAdminEditing = auth.canManageClientResourceTalent() && !!props.user;

    const requestEndpoint = useMemo(() => {
        if (!isAdminEditing || !props.user?.id || !auth?.hasSupplierClientOrg()) {
            return '/api/sessions/update'
        }
        return `/api/clients/${auth.supplierOrg()?.id}/users/${props.user.id}/update`
    }, [auth, isAdminEditing]);

    const authTalentUser = useMemo(() => {
        let user = {...(props.user || auth || {})};
        if (user.phone) {
            let phoneNumber = user.phone.replace(/\D/g, '');
            if (phoneNumber?.length === 10 && !user.phone.includes('+')) {
                user.phone = "+1 " + user.phone;
            }
        }
        return User.make({...user})
    }, [props.user, auth, isDisabled]);

    const [talentUser, setTalentUser] = useState(() => authTalentUser);

    const [activeSteps, setActiveSteps] = useState(() => talentUser?.currentProfileSteps());

    const profileProgress = useMemo(() => auth?.profileProgress(), [auth, isDisabled])

    // Display-only progress summary (based on the saved user, not unsaved edits)
    const journeyProgress = useMemo(
        () => getJourneyProgress(authTalentUser, activeSteps),
        [authTalentUser, activeSteps, isDisabled]
    );
    const isApplicationReady = !authTalentUser?.isApplicationApproved?.()
        && !authTalentUser?.didSubmitApplication?.()
        && !!authTalentUser?.hasCompletedApplication?.()
        && journeyProgress.requiredRemaining.length === 0;

    // Auto-open the "Submit for approval" modal only the first time the
    // profile becomes ready (not after every save). After that, users submit
    // from the sidebar card or footer button when they're done.
    const profileSubmitPrompted = useRef(!!authTalentUser?.isProfileReadyForApproval?.());
    const shouldPromptProfileSubmit = (savedUser) => {
        const candidate = savedUser || talentUser;
        if (profileSubmitPrompted.current) return false;
        if (!(candidate?.isApplicationApproved?.() && candidate.isProfileReadyForApproval?.())) return false;
        profileSubmitPrompted.current = true;
        return true;
    };

    // Lets builder-only styles (rounded buttons) reach popups portaled to <body>
    useEffect(() => {
        document.body.classList.add('talent-application-open');
        return () => document.body.classList.remove('talent-application-open');
    }, []);

    // Let the applicant know the moment their application becomes ready
    const wasApplicationReady = useRef(isApplicationReady);
    useEffect(() => {
        if (isApplicationReady && !wasApplicationReady.current && !isAdminEditing) {
            TB.toast('Your application is ready! Review and submit whenever you\'re set.');
        }
        wasApplicationReady.current = isApplicationReady;
    }, [isApplicationReady]);
    const [lastSavedStep, setLastSavedStep] = useState(() => talentUser?.currentApplicationStep())

    const currentStepIndex = useMemo(() => {
        return TB.findIndex(activeSteps, (step, index) => {
            return  location.pathname.includes(step.getPath(!isAdminEditing ? '/talent/application' : ''))
        })
    }, [location?.pathname, activeSteps]);

    const currentStep = useMemo(() => {
        return activeSteps[currentStepIndex]
    }, [currentStepIndex, activeSteps]);

    const nextStep = useMemo(() => {
        if (currentStepIndex !== -1) {
            return activeSteps[currentStepIndex + 1]
        }
        return activeSteps[currentStepIndex]
    }, [currentStepIndex, activeSteps]);

    const prevStep = useMemo(() => {
        if (currentStepIndex !== -1) {
            return activeSteps[currentStepIndex - 1]
        }

        return activeSteps[currentStepIndex]
    }, [currentStepIndex, activeSteps]);

    useEffect(() => {
        if (!talentUser || currentStepIndex !== -1) {
            return;
        }
        if (!lastSavedStep) {
            // do something else??
        }

        return goToStep(lastSavedStep, true)
    }, [talentUser, currentStepIndex])

    /**
     * Run a step's validation without its own requirement alerts/toasts.
     * Used for Save & exit so the user only sees the single
     * "exit without saving?" confirmation instead of two alerts in a row.
     */
    const validateQuietly = async (validate) => {
        const originals = {alert: TB.alert, sysAlert: TB.sysAlert, toast: TB.toast};
        TB.alert = () => Promise.resolve(false);
        TB.sysAlert = () => Promise.resolve(false);
        TB.toast = () => {};
        try {
            return await validate();
        } finally {
            TB.alert = originals.alert;
            TB.sysAlert = originals.sysAlert;
            TB.toast = originals.toast;
        }
    }

    const isReviewing = () => {
        return location.state?.fromReview
    }

    const saveAndExitInvalidForm = async() => {
        if (TB.AlertIsShowing) {
            await TB.event('Alert@hide');
            await TB.wait(400);
            console.log('saveAndExitInvalidForm', 'WAITING')
        }
        let exitAnyways = await TB.sysAlert('talent.application_save_and_exit_button_clicked');

        // console.log('saveAndExitInvalidForm', {exitAnyways})
        if (exitAnyways) {
            if (isAdminEditing) {

                console.log('saveAndExitInvalidForm', "/supplier/dashboard")
                return navigate('/supplier/dashboard', {replace: true})
            }
            // console.log('saveAndExitInvalidForm', talentUser?.hasCompletedProfile(), appropriate())
            // Live profiles go straight back to the dashboard; everyone else
            // gets the "saved" summary of what's left.
            if (talentUser?.isProfileApproved?.()) {
                return navigate('/talent/dashboard')
            }

            return setSaveExitOpen(true);
        }
    }

    const goToStep = (step, replace = false) => {
        let route = {
            ...location,
            pathname: step.getPath(!isAdminEditing ? '/talent/application' : '.')
        }
        if (step === BioStep &&  talentUser?.resumeDocument?.() && !auth.generated_account_data && !talentUser.isApplicationApproved()) {
            route.search = '?importResume=1'
        }
        if (step === ReviewStep && !talentUser?.hasCompletedApplication()) {
            return TB.alert('warning', 'Application Not Finished',
                'You haven\'t finished all the required steps. Please complete the remaining steps in order to submit your application.',  [{title: 'Ok'}]
            )
        }
        if (replace) {
            return navigate(route, {replace: true});
        }
        return navigate(route)
    }

    // const toggleResumeModal = async(show = false) => {
    //     const search = show ? '?uploadResume=true' : '';
    //
    //     navigate({pathname: location.pathname, search}, {replace: true});
    //
    //     if (!show) {
    //         await TB.wait(1500);
    //
    //         await resumeModalRef?.current?.resetState();
    //     }
    // }

    const toggleDisabled = (submitDisabled) => {
        return setStepSubmitDisabled(submitDisabled)
    }

    const updateTalentApplicationMutation = useMutation(
        ['talentUpdate', talentUser.id],
            params => talentUpdateRequest(params, requestEndpoint)
    )

    const onUpdateTalent = async(updatedTalent) => {
        let user = updatedTalent;
        if (!isAdminEditing || talentUser.id === auth.id) {
            user = await updateAuth(updatedTalent)
        } else if (props.onUpdateTalent) {
            user = await props.onUpdateTalent(updatedTalent)
        }
        return setTalentUser(user);
    };

    const isEditingInterviewStep = useMemo(() => {

        if (!location.pathname.includes('/video-interview')) {
            return false;
        }

        const match = matchPath({path: 'talent/application/video-interview/*'},location.pathname);
        console.log('match', match)
        return match?.params['*']?.length > 0;
    }, [location.pathname, isDisabled]);

    const onSubmit = async(params, exitApplication = false, skipGoToNext = false) => {
        // if (stepSubmitDisabled && exitApplication) {
        //     let exitNow = await TB.sysAlert('talent.application_save_and_exit_button_clicked');
        //     if (exitNow) return logout();
        //     else return;
        // }

        try {
            let invalidField = false;
            if (routeRef?.current
                && TB.isFunction(routeRef.current.invalidFields)) {
                invalidField = await routeRef.current?.invalidFields();
            }

            if (invalidField) {

                if (exitApplication) {
                    return saveAndExitInvalidForm();
                }
                return
            }
        } catch (e) {
            console.warn('ERROR: onSubmitValidateCheck', e);
        }


        let user = talentUser;

        let completedStep = await currentStep?.hasCompleted(
            User.make({
                ...params
            })
        );

        if ((completedStep || user.getFullProfileStepsForTalent().includes(currentStep)) && params) {


            const runValidation = () => currentStep?.getValidProps(
                User.make({
                    ...params
                }), skipGoToNext
            );
            let validated = exitApplication
                ? await validateQuietly(runValidation)
                : await runValidation();

            if (!validated) {
                await TB.event("TalentApp@Submit");
                if (exitApplication) {

                    return saveAndExitInvalidForm();
                }
                else return;
            }
            await setIsDisabled(true)

            try {
                user = await updateTalentApplicationMutation.mutateAsync(validated);

            } catch (e) {
                user = false;
                console.warn('ERROR: onSubmit', e);

                if (TB.auth().TalentUpdateRequest?.didFail &&
                    (TB.auth().TalentUpdateRequest?.errorType === TB.Request.ErrorTooLong ||
                        TB.auth().TalentUpdateRequest?.errorType === TB.Request.ErrorLocal)) {

                    await setIsDisabled(false)

                    return await TB.alert('error',
                        TB.trans('api.system_maintenance.title'),
                        TB.trans('api.system_maintenance.message', {code: 'XXX'}), [
                            {
                                title: 'Okay',
                            }
                        ]
                    )
                }
            }

            if (!user) {
                /*
                * Update form with server errors
                * */
                // console.log('onSubmit', TB.auth().TalentUpdateRequest)
                if (await TB.auth().talentHasInputResponseError()) {
                    await routeRef.current?.handleInputResponseError(TB.auth().TalentUpdateRequest);
                }


                if (exitApplication) return saveAndExitInvalidForm();

                return setIsDisabled(false)
            }
        }

        if (!completedStep && !user.getFullProfileStepsForTalent?.().includes(currentStep)) {
            const runValidation = () => currentStep.getValidProps(
                User.make({
                    ...params
                }), skipGoToNext
            );
            let validated = exitApplication
                ? await validateQuietly(runValidation)
                : await runValidation();

            if (!validated) {
                await TB.event("TalentApp@Submit");
                if (exitApplication) return saveAndExitInvalidForm();
                else return;
            }
        }
        await onUpdateTalent(user);

        // Display-only: remember that sections with pre-filled defaults were confirmed
        markSectionConfirmed(user, currentStep);

        if (isReviewing()) {
            await setIsDisabled(false)

            return goToStep(ReviewStep, true)
        }

        if (exitApplication) {
            await setIsDisabled(false);

            if (user.isProfileApproved?.()) {
                return navigate(appropriate())
            }
            return setSaveExitOpen(true);
        }

        if (skipGoToNext) {
            await setIsDisabled(false);
            console.log('skipppp', user)
            return user;
        }

        if (nextStep) {
            if (currentStepIndex > activeSteps.indexOf(lastSavedStep)) {
                await setLastSavedStep(() => currentStep)
            }
            await setIsDisabled(false)

            await goToStep(nextStep);

            if (shouldPromptProfileSubmit(user)) {
                let params = new URLSearchParams(searchParams)
                params.set('submitForApproval', '1');
                setSearchParams(params);
            }
            return;
        }

        await setIsDisabled(false)

        let redirect;

        if (user.isTalentSupplierResource?.() && user?.isApplicationApproved() && currentStep === ReviewStep) {
            await setLastSavedStep(() => user?.currentApplicationStep())
            await setActiveSteps(() => user?.currentProfileSteps())
            return await TB.alert('success', 'Supplier Profile Updated Successfully',
                'Your profile is now completed. You can edit this information at any time', [
                    {title: 'Go to dashboard', click: () => navigate('/talent/dashboard', {replace: true})},
                    {
                        title: 'Continue editing', click: () => window.scrollTo({
                            top: 0,
                            left: 0,
                            behavior: 'smooth'
                        })
                    }
                ]
            );
        }
        // Application just submitted: show the confirmation pop-up first.
        // "Done" then continues to the under-review screen (see SubmitApplicationModal).
        if (currentStep === ReviewStep && !isAdminEditing
            && user.didSubmitApplication?.() && !user.isApplicationApproved?.()) {
            return setAppSubmitStep('submitted');
        }

        if (!TB.auth().isAppropriate(location)) {
            redirect = TB.auth().appropriate();
        }

        if (redirect) {
            navigate(redirect)
        }

        if (shouldPromptProfileSubmit(user)) {
            let params = new URLSearchParams(searchParams)
            params.set('submitForApproval', '1');
            setSearchParams(params);
        }
    }

    // if (!currentStep) {
        // <Navigate to={{
        //     ...location,
        //     pathname: `${!isAdminEditing ? '/talent/application' : '.'}${(lastSavedStep || ApplicationStartStep).path}`,
        // }} replace/>
        // return null
    // }

    // Fix for showing progress text when percent is small, i.e 10% round up to 15
    let progressPercent = profileProgress > 0 ? Math.max(profileProgress, 15) : profileProgress;

    let isLastStep = currentStepIndex === activeSteps.length - 1,
        lastStepRemaining = talentUser.lastStepRemaining();

    const isReviewStep = location.pathname.endsWith('/review');
    const isProfilePhase = !!authTalentUser?.isApplicationApproved?.();
    const isLive = !!authTalentUser?.isProfileApproved?.();
    const stripPhase = isLive ? 'live' : (isProfilePhase ? 'profile' : 'application');
    const optionalSections = isLive ? getOptionalSuggestions(authTalentUser) : null;

    // "Open to Work" (is_available) — same behaviour as the dashboard toggle
    const [availabilityBusy, setAvailabilityBusy] = useState(false);
    const setOpenToWork = async (value) => {
        if (!value && !(await TB.sysAlert('talent.turn_off_availability_warning'))) return;

        setAvailabilityBusy(true);
        try {
            const updated = await talentUpdate({ is_available: value });
            // Keep the in-progress form copy in sync so a later step save can't revert it
            if (updated) setTalentUser(current => User.make({ ...current, is_available: updated.is_available }));
        } finally {
            setAvailabilityBusy(false);
        }
    };

    // Opens ReadyToSubmitProfileModal (it listens for the ?submitForApproval param)
    const openSubmitForApproval = () => {
        if (searchParams.has('submitForApproval') || isDisabled) return;
        let params = new URLSearchParams(searchParams);
        params.set('submitForApproval', '1');
        setSearchParams(params);
    };
    // const isInterviewStep = location.pathname.includes('/video-interview');


    return (
        <>
            {currentStep?.StepTitle && (
                <Helmet>
                    <title>{String(currentStep?.StepTitle)} - Profile | Wripple</title>
                </Helmet>
            )}

            <ReadyToSubmitProfileModal onSubmitProfileApproval={params => {
                setTalentUser(User.make({
                    ...talentUser,
                    ...params
                }))
            }}/>

            <SaveExitModal
                open={saveExitOpen}
                user={authTalentUser}
                progress={journeyProgress}
                onKeepWorking={() => setSaveExitOpen(false)}
                onLogout={() => logout()}
                onDashboard={() => navigate('/talent/dashboard')}
            />

            <SubmitApplicationModal
                step={appSubmitStep}
                sectionCount={journeyProgress.requiredCount}
                busy={isDisabled}
                onCancel={() => setAppSubmitStep(null)}
                onSubmit={() => onSubmit(talentUser)}
                onDone={() => navigate(appropriate())}
                onRefer={() => navigate('/talent/under-review', {state: {refer: true}})}
            />

            {/* {!auth.isProfileApproved?.() && (
                <div className="pt-2.5 pb-3 bg-white border-y">
                    <ApplicationStepper />
                </div>
            )} */}

            <div className={"talent-application-builder " + (isEditingInterviewStep ? '' : ("talent-application-container " + (!currentStep?.showFormButtons() && 'review-application')))}>
                {!isEditingInterviewStep && !location.pathname.includes('/get-started') && (
                    (!isAdminEditing || talentUser.id === auth.id) ? (
                        <>
                            <ApplicationStatusStrip
                                progress={journeyProgress}
                                phase={stripPhase}
                                optional={optionalSections && { added: optionalSections.addedCount, total: optionalSections.all.length }}
                                openToWork={isLive && !isAdminEditing
                                    ? { checked: !!authTalentUser.is_available, busy: availabilityBusy, onChange: setOpenToWork }
                                    : null}
                                isReady={isApplicationReady || !!authTalentUser?.isProfileReadyForApproval?.()}
                                previewHref={isProfilePhase && !isAdminEditing ? `/talent-profiles/${authTalentUser.id}` : null}
                                showSaveAndExit={currentStep !== ReviewStep && !!currentStep?.showFormButtons?.() && !isReviewing()}
                                disabled={isDisabled || stepSubmitDisabled}
                                onSaveAndExit={() => onSubmit(talentUser, true)}
                            />
                            {!isAdminEditing && (
                                <ProfileStatusBar
                                    talentUser={authTalentUser}
                                    progress={journeyProgress}
                                    disabled={isDisabled || stepSubmitDisabled || searchParams.has('submitForApproval')}
                                    onSubmitForApproval={openSubmitForApproval}
                                    onTurnOnOpenToWork={() => setOpenToWork(true)}
                                />
                            )}
                        </>
                    ) : (
                        <div className="talent-application-progress">
                            <div className="h-[30px] self-stretch bg-danger flex flex-col items-center justify-center">
                                <p className="talent-app-progress-title talent-supplier-resource text-white text-center">
                                    <span className="font-medium">NOTE: Currently editing Talent Resource Profile
                                        for:&nbsp;</span>
                                    <span className="font-bold text-black">{talentUser.fullName()}</span>
                                </p>
                            </div>
                        </div>
                    )
                )}

                {/* {(isAdminEditing || talentUser.id !== auth.id) && (
                    <div className="talent-application-progress">
                        <div className="h-[30px] self-stretch bg-danger flex flex-col items-center justify-center">
                            <p className="talent-app-progress-title talent-supplier-resource text-white text-center">
                                <span className="font-medium">NOTE: Currently editing Talent Resource Profile
                                    for:&nbsp;</span>
                                <span className="font-bold text-black">{talentUser.fullName()}</span>
                            </p>
                        </div>
                    </div>
                )} */}
                    
                <div className={isEditingInterviewStep ? '' : "talent-application-form-container"}>

                    {!isEditingInterviewStep && currentStep && (currentStep.StepInfo || currentStep === ReviewStep) && (
                        <ApplicationSideNavBar
                            {...props}
                            steps={activeSteps}
                            disabled={isDisabled}
                            currentStep={currentStep}
                            currentStepIndex={currentStepIndex}
                            onStepNavigate={step => (!isDisabled && goToStep(step))}/>
                    )}


                    <div className={isEditingInterviewStep ? '' : (currentStep?.path === '/get-started' ? "w-full" : "w-full lg:w-8/12 xl:w-9/12 pt-6 md:pt-0")}>

                        <Routes>
                            <Route index element={<Navigate
                                to={{...location, pathname: TB.replace(lastSavedStep?.path, '/', '')}} replace/>}/>
                            {activeSteps.map(Step => (
                                <Route key={Step.path} path={TB.replace(Step.path, '/', '') + '/*'} element={(
                                    <Step
                                        ref={routeRef}
                                        stepNeeded={lastStepRemaining}
                                        user={talentUser}
                                        authUser={authTalentUser}
                                        isEditingInterviewStep={isEditingInterviewStep}
                                        onEditingChildForm={toggleDisabled}
                                        isAdminEditing={!!isAdminEditing}
                                        handleEditing={toggleDisabled}
                                        disabled={isDisabled}
                                        onSubmitChild={(params) => onSubmit(params, false, true)}
                                        onSubmit={onSubmit}
                                        onChange={params => {
                                            console.log('onchangetalentapp', params)
                                            setTalentUser(User.make({
                                                ...talentUser,
                                                ...params
                                            }))
                                        }}
                                    />
                                )}/>
                            ))}
                            {/*<Navigate to={{...location, pathname: TB.replace(lastSavedStep?.path, '/', '')}} replace/>*/}
                            <Route path={'*'} element={<PageNotFound exitText="Back to Application" exitLink={{...location, pathname: TB.replace(lastSavedStep?.path, '/', '')}} />}/>

                        </Routes>

                        {/* Back / Continue — sits under the step's form (Save & exit lives in the strip) */}
                        {!isEditingInterviewStep && currentStep?.showFormButtons() && (
                            <div className={isReviewStep
                                ? "talent-application mx-auto !pt-2 pb-10" // matches ReviewStep's centered layout
                                : "px-4 md:px-8 xl:w-10/12 w-full mr-auto ml-0 pt-2 pb-10"}>
                                <div className="flex flex-row flex-wrap items-center justify-between gap-3">
                                    {isReviewing() ? (
                                        <button
                                            type="button"
                                            disabled={isDisabled || stepSubmitDisabled}
                                            onClick={() => onSubmit(talentUser, true)}
                                            className="btn btn-outline-dark bg-white hover:!bg-gray-100 hover:!text-gray-900 active:!text-gray-900">
                                            Back to review
                                        </button>
                                    ) : (!talentUser.hasCompletedProfile() || currentStepIndex > 0) ? (
                                        <button
                                            type="button"
                                            onClick={() => goToStep(prevStep, true)}
                                            disabled={isDisabled || stepSubmitDisabled || currentStepIndex === 0}
                                            className="btn btn-outline-dark bg-white hover:!bg-gray-100 hover:!text-gray-900 active:!text-gray-900">
                                            Back
                                        </button>
                                    ) : <span />}

                                    <div className="ml-auto flex flex-row flex-wrap items-center justify-end gap-3">
                                        {/* Admins editing a resource don't get the profile status bar */}
                                        {isAdminEditing && !isReviewing() && talentUser.isProfileReadyForApproval() && (
                                            <button
                                                type="button"
                                                disabled={isDisabled || stepSubmitDisabled || searchParams.has('submitForApproval')}
                                                onClick={openSubmitForApproval}
                                                className="btn btn-outline-primary text-primary font-semibold">
                                                Submit for Approval →
                                            </button>
                                        )}

                                        {!isLastStep ? (
                                            <button
                                                type="button"
                                                disabled={isDisabled || stepSubmitDisabled}
                                                onClick={() => onSubmit(talentUser)}
                                                className="btn btn-primary">
                                                {isReviewing() ? 'Save changes' : 'Continue'}
                                            </button>
                                        ) : (!talentUser.didSubmitApplication() && talentUser.hasCompletedApplication()) ? (
                                            <button
                                                type="button"
                                                disabled={isDisabled || stepSubmitDisabled}
                                                onClick={() => setAppSubmitStep('confirm')}
                                                className="btn btn-primary inline-flex items-center justify-center gap-2">
                                                Submit application <ArrowRight className="h-4 w-4" />
                                            </button>
                                        ) : (
                                            // Last builder section: nothing to continue to, so save and show the summary
                                            <button
                                                type="button"
                                                disabled={isDisabled || stepSubmitDisabled}
                                                onClick={() => onSubmit(talentUser, true)}
                                                className="btn btn-primary">
                                                Save &amp; exit
                                            </button>
                                        )}
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                </div>

                <Modal
                    okText={null}
                    zIndex={199}
                    footer={null}
                    cancelText={null}
                    title={null}
                    // className="schedule-meeting-modal"
                    open={!talentUser.isApplicationApproved() && searchParams?.has('importResume')}
                    closeable={false}
                    maskClosable={false}
                    style={{top: 20}}
                    destroyOnClose={true}
                    width={900}
                    closeIcon={<div className="hidden pointer-events-none"/>}
                    // onCancel={}
                >
                    <ResumeUploadModal
                        onRequestClose={() => {
                            navigate({
                                pathname: location.pathname,
                            }, {replace: true})
                        }}
                                onUpdate={async (props = {}) => {
                                    let user = false;
                                    await setIsDisabled(true)

                                    try {
                                        user = await updateTalentApplicationMutation.mutateAsync(props);

                                    } catch (e) {
                                        console.warn('ERROR', e)
                                        user = false;
                                    }
                                    if (!user) {
                                        await setIsDisabled(false)

                                        return TB.showServerErrorToast();
                                    }

                                    if (user) {
                                        await user.syncResumeImportLocalStorage(props);


                                        // let params = new URLSearchParams(searchParams);
                                        // params.delete('importResume')
                                        // await setSearchParams(params)

                                        await onUpdateTalent(user);

                                        await setIsDisabled(false)
                                        navigate({
                                            pathname: location.pathname,
                                        }, {replace: true})
                                        await TB.sysAlert('talent.resume_imported_success')
                                    }
                                }}
                                disabled={isDisabled}
                    />

                </Modal>
                {/*<Modal*/}
                {/*    onCloseAction={() => toggleResumeModal(false)}*/}
                {/*    onUploadSuccess={response => {*/}
                {/*        console.log('onUploadSuccess', response);*/}
                {/*    }}*/}
                {/*    modalClass="resume-upload-modal"*/}
                {/*    isVisible={queryParams.has('uploadResume')}>*/}

                {/*    <ResumeUploadModal*/}
                {/*        ref={resumeModalRef}*/}
                {/*        onRequestClose={() => toggleResumeModal(false)}*/}
                {/*        onUpdate={async (props = {}) => {*/}
                {/*            let user = false;*/}
                {/*            await setIsDisabled(true)*/}

                {/*            try {*/}
                {/*                let updates = {};*/}
                {/*                for (let [propName, value] of Object.entries(props)) {*/}
                {/*                    updates[propName] = value || [];*/}

                {/*                    if (talentUser[propName]?.length) {*/}
                {/*                        updates[propName] = [*/}
                {/*                            ...updates[propName],*/}
                {/*                            ...talentUser[propName],*/}
                {/*                        ];*/}
                {/*                    }*/}
                {/*                }*/}
                {/*                user = await updateTalentApplicationMutation.mutateAsync(updates);*/}

                {/*            } catch (e) {*/}
                {/*                user = false;*/}
                {/*            }*/}
                {/*            if (!user) {*/}
                {/*                await setIsDisabled(false)*/}

                {/*                return TB.showServerErrorToast();*/}
                {/*            }*/}

                {/*            if (user) {*/}
                {/*                await toggleResumeModal(false);*/}

                {/*                await onUpdateTalent(user);*/}

                {/*                await setIsDisabled(false)*/}

                {/*                await TB.sysAlert('talent.resume_imported_success')*/}
                {/*            }*/}
                {/*        }}*/}
                {/*        disabled={isDisabled}*/}
                {/*    />*/}

                {/*</Modal>*/}
            </div>
        </>
    );
}

export default TalentApplication